/**
 * What a barcode scanner hands the app is not quite the barcode.
 *
 * Handheld scanners are configured with prefix and suffix characters -- a
 * carriage return, a tab, and on the dock's scanner a period -- so Marvin's
 * "VDD629" arrives as "VDD629." and matches nothing. Code 39 readers can also
 * transmit the "*" start/stop delimiters, and some send Shift+digit for
 * digits, producing "!@#" for "123". None of that is part of the identifier.
 * Every place a scan enters the app runs it through here first, and the
 * matchers normalise the stored side the same way, so the comparison is
 * identifier to identifier.
 */

const SHIFT_TO_DIGIT = {
  ")": "0",
  "!": "1",
  "@": "2",
  "#": "3",
  $: "4",
  "%": "5",
  "^": "6",
  "&": "7",
  "*": "8",
  "(": "9",
};

/**
 * Strip scanner prefix/suffix artefacts from a scanned code, in an order
 * that keeps each fix from eating the next: Code 39's "*" delimiters come
 * off first (so they are never mistaken for a shifted 8), shifted-digit
 * characters are mapped back to digits (so a code that is all digits
 * survives the next step), and then whatever non-alphanumeric characters
 * remain at either end go (".", ",", CR, tab). Internal punctuation stays:
 * "LBL-04100058A-item-1" and "WAREHOUSE:ZONE" are intact.
 */
export function normalizeScannedCode(code) {
  return String(code == null ? "" : code)
    .trim()
    .replace(/^\*+|\*+$/g, "")
    .replace(/[)!@#$%^&*(]/g, (ch) => SHIFT_TO_DIGIT[ch] || ch)
    .replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, "");
}

/**
 * A piece mark after a Marvin tracking number. A screen label on tracking
 * number VKI917 -- two screens, TrackingQty 2 -- read "VKI917A1", while the
 * shipping feed, and so the Expected list, only ever say "VKI917". A letter
 * and one to three digits is the mark; digits alone are not ("VKI9171" is a
 * different number).
 */
const PIECE_MARK = /^[A-Za-z]\d{1,3}$/;

/**
 * True when a scanned code and a stored identifier name the same thing:
 * equal after normalisation, or one is a suffix of the other (a scanner can
 * read a truncated code, and some vendors print the identifier with a
 * prefix the feed omits), or the scan is the stored identifier followed by a
 * piece mark. Empty strings never match.
 */
export function codesMatch(scanned, stored) {
  const a = normalizeScannedCode(scanned);
  const b = normalizeScannedCode(stored);
  if (!a || !b) return false;
  return (
    a === b ||
    a.endsWith(b) ||
    b.endsWith(a) ||
    (a.startsWith(b) && PIECE_MARK.test(a.slice(b.length)))
  );
}

/**
 * Of the barcodes a detector found in one camera frame, the one nearest the
 * frame's middle: the receiver aims at the code they want, and a Quaker label
 * carries two (the carton's, and a smaller one naming only the order).
 * `barcodes` are the Shape Detection API's results -- `rawValue` and a
 * `boundingBox` in the frame's own pixels. Null when none has a value.
 */
function nearestTheCentre(barcodes, width, height) {
  let best = null;
  let bestDistance = Infinity;
  (barcodes || []).forEach((barcode) => {
    if (!barcode || !barcode.rawValue) return;
    const box = barcode.boundingBox || {};
    const dx = (box.x || 0) + (box.width || 0) / 2 - (width || 0) / 2;
    const dy = (box.y || 0) + (box.height || 0) / 2 - (height || 0) / 2;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      best = barcode;
      bestDistance = distance;
    }
  });
  return best;
}

/** The value of the barcode nearest the middle of the frame, or null. */
export function centremost(barcodes, width, height) {
  const best = nearestTheCentre(barcodes, width, height);
  return best ? best.rawValue : null;
}

/**
 * The symbologies the dock's own labels are printed in: the Kolbe, Quaker and
 * Windsor carton labels and the warehouse location labels are Code 128, the
 * app's own reprints are Code 39. Code 93 and QR stay for labels not yet seen.
 * None of them is a symbology a stray patch of bars decodes as by accident
 * (Code 128 and Code 93 carry check characters, Code 39 its start/stop
 * pattern, QR error correction). Names are the Shape Detection API's `format`
 * strings.
 */
export const WAREHOUSE_FORMATS = ["code_128", "code_39", "code_93", "qr_code"];

/**
 * The retail and industrial symbologies that check little or nothing: UPC-E
 * and EAN-8 are eight digits ending in a single check digit (one random read
 * in ten passes it), ITF and Codabar have no required check at all. A Code 128
 * label read as one of these is the dock's false positive (see FORMATS in
 * BarcodeScanner.vue), so they need a longer run of agreeing reads than the
 * rest. UPC-A and EAN-13 are twelve and thirteen digits and keep the ordinary
 * count; priority alone keeps them out of a frame with a warehouse code in it.
 */
export const WEAK_FORMATS = ["upc_e", "ean_8", "itf", "codabar"];

/**
 * Of the barcodes in one camera frame, the code to count: `{ value, format }`
 * or null.
 *
 * When the frame holds a read in a warehouse symbology, every other read in it
 * is dropped before the choice -- a Code 128 label is exactly what the retail
 * readers mistake for a UPC-E -- and the one nearest the middle of what is left
 * wins, as in centremost. A frame of retail codes alone keeps them all: they
 * are only counted, never taken on one frame (see createAgreement).
 */
export function chooseBarcode(barcodes, width, height) {
  const read = (barcodes || []).filter(
    (barcode) => barcode && barcode.rawValue
  );
  const warehouse = read.filter((barcode) =>
    WAREHOUSE_FORMATS.includes(barcode.format)
  );
  const chosen = nearestTheCentre(
    warehouse.length ? warehouse : read,
    width,
    height
  );
  return chosen
    ? { value: chosen.rawValue, format: chosen.format || null }
    : null;
}

/**
 * Agreeing reads in a row before a code is taken; weak symbologies need more.
 * The weak count is about a second of passes: a UPC-E read off a Code 128
 * label that the lens is held steady on repeats every pass, so three agreeing
 * reads (300 ms) only delay the misread, while a second of them leaves the
 * real code time to decode and take the count over. Nothing in the app scans a
 * weak symbology on purpose (see FORMATS in BarcodeScanner.vue), so the wait
 * is cheap.
 */
export const AGREEMENT_PASSES = 2;
export const WEAK_AGREEMENT_PASSES = 8;

/**
 * How long a read is held while the camera finds nothing, or something it
 * cannot make out: a shaky hand loses the label for a pass or two between two
 * good reads. A read is held through this many passes that do not repeat it,
 * and through any stretch of AGREEMENT_GAP_MS, and lapses only when both are
 * used up.
 *
 * Both, because neither clock is steady. A pass on the phone's detector is
 * 100 ms plus however long detect() takes, which is device business (400 ms on
 * a slow phone: no two reads would ever be 500 ms apart, whatever the hand
 * does), so there the passes are the measure. The JavaScript reader reports
 * every failed decode as a pass of its own, back to back, so a lost label is
 * many passes in a few hundred milliseconds, and there the time is.
 */
export const AGREEMENT_MISSED_PASSES = 3;
export const AGREEMENT_GAP_MS = 500;

/**
 * Takes a camera's reads only once they agree. One frame is not a scan: the
 * 2026-10-05 dock misread (a Code 128 package sticker as the UPC-E 14530115)
 * was a single frame's worth of noise, and a code that is really in front of
 * the lens turns up again on the next pass.
 *
 * `see(choice, now)` is given the code chosen from each pass (`{ value,
 * format }` from chooseBarcode, or null for a pass that found nothing) and
 * returns that code once the same value in the same symbology has been chosen
 * in AGREEMENT_PASSES passes in a row (WEAK_AGREEMENT_PASSES for the weak
 * symbologies), else null. A different code restarts the count at one. A pass
 * with nothing in it neither counts nor restarts: the read is held (see
 * AGREEMENT_MISSED_PASSES), so a pass or two lost to a shaky hand cost only
 * their own time.
 *
 * A retail or weak read does not restart a warehouse code being counted
 * either: it is a pass the code was missing from, like an empty one. That is
 * the same rule chooseBarcode applies within one frame, carried across frames,
 * for a label that decodes as itself on one pass and as a UPC-E on the next
 * (read strictly in turn, the real code would otherwise never reach its second
 * pass). Two different warehouse codes still restart each other: the receiver
 * has moved from one label to another.
 *
 * Once a code is returned the count starts over, so a camera held on the same
 * label (the continuous scanner) has to agree with itself again for the next
 * one.
 */
export function createAgreement() {
  // The code being counted: { key, warehouse, count, at, missed }.
  let seen = null;
  return {
    see(choice, now = Date.now()) {
      const held =
        seen &&
        (seen.missed <= AGREEMENT_MISSED_PASSES ||
          now - seen.at <= AGREEMENT_GAP_MS);
      const read = choice && choice.value ? choice : null;
      const warehouse = !!read && WAREHOUSE_FORMATS.includes(read.format);
      if (!read || (held && seen.warehouse && !warehouse)) {
        if (seen) seen.missed += 1;
        return null;
      }
      const key = `${read.format || ""}|${read.value}`;
      if (held && seen.key === key) {
        seen.count += 1;
      } else {
        seen = { key, warehouse, count: 1 };
      }
      seen.at = now;
      seen.missed = 0;
      const needed = WEAK_FORMATS.includes(read.format)
        ? WEAK_AGREEMENT_PASSES
        : AGREEMENT_PASSES;
      if (seen.count < needed) return null;
      seen = null;
      return read;
    },
  };
}
